<template>
    <div class="nevs-content" v-if="screenReady">
        <div v-if="productPopup.show" class="nevs-popup-container">
            <div class="nevs-popup">
                <div class="nevs-popup-text">
                    <NevsTextField v-model="productPopup.data.name" :error="productPopup.validation.name"
                                   :label="'*' + $LANG.Get('fields.nameHr') + ':'"></NevsTextField>
                    <NevsTextField v-model="productPopup.data.name_en"
                                   :label="$LANG.Get('fields.nameEn') + ':'"></NevsTextField>
                    <NevsTextField v-model="productPopup.data.code"
                                   :label="$LANG.Get('fields.code_internal') + ':'"></NevsTextField>
                    <NevsSelect :minimumSearchLength="3" v-model="productPopup.data.code_kpd" :ajax="'select/product-codes'"
                                :label="$LANG.Get('fields.code_kpd') + ':'"></NevsSelect>
                    <NevsSelect v-model="productPopup.data.measurement_unit_id" :ajax="'select/measurement-units'"
                                :label="$LANG.Get('fields.measurementUnit')"></NevsSelect>
                    <NevsNumberField v-model="productPopup.data.price"
                                     :error="productPopup.validation.price"
                                     :label="'*' + $LANG.Get('fields.priceWithoutTax').split('%currency%').join($HELPERS.GetDefaultCurrency()) + ':'"
                                     class="product-popup-half"></NevsNumberField>
                    <NevsNumberField v-show="$store.state.user.client.tax_system" v-model="productPopup.data.tax"
                                     :label="'*' + $LANG.Get('fields.tax') + ':'"
                                     class="product-popup-half"></NevsNumberField>
                    <NevsCheckbox v-model="productPopup.data.return_fee" :label="$LANG.Get('fields.returnFee')"></NevsCheckbox>
                    <NevsNumberField v-if="productPopup.data.return_fee" v-model="productPopup.data.return_fee_amount"
                                     :error="productPopup.validation.return_fee_amount"
                                     :label="'*' + $LANG.Get('fields.returnFeeAmount').split('%currency%').join($HELPERS.GetDefaultCurrency()) + ':'"></NevsNumberField>
                    <div v-if="productPopup.data.return_fee" class="return-fee-disclaimer">
                        <i class="fa-solid fa-circle-info"></i>
                        <span>{{ $LANG.Get('texts.returnFeeDisclaimer') }}</span>
                    </div>
                    <NevsCheckbox v-if="$store.state.user.client.plus" v-model="productPopup.data.wares_expenditure" :label="$LANG.Get('fields.waresExpenditure')"></NevsCheckbox>
                    <div v-if="$store.state.user.client.plus && productPopup.data.wares_expenditure">
                        <table class="wares-details-table">
                            <tr v-for="(ware_detail, key) in productPopup.data.wares_expenditure_details" :key="key">
                                <td><NevsSelect v-model="ware_detail.ware_id" :ajax="'select/wares-with-measurement-units'"></NevsSelect></td>
                                <td style="width: 150px;"><NevsNumberField v-model="ware_detail.quantity" :decimal-places="3"></NevsNumberField></td>
                                <td style="width: 20px;"><span :title="$LANG.Get('tooltips.delete')" class="group-button" @click="productPopup.removeWare(key)"><i
                                    class="fa-solid fa-trash"></i></span></td>
                            </tr>
                        </table>
                        <NevsButton style="margin-left: 7px; margin-top: 10px;" @click="productPopup.addWare()"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.addWare') }}</NevsButton>
                    </div>
                    <NevsCheckbox v-model="productPopup.data.active" :label="$LANG.Get('fields.activeM')"></NevsCheckbox>
                </div>
                <div class="nevs-popup-buttons">
                    <NevsButton class="error" @click="productPopup.cancel()"><i class="fa-solid fa-xmark"></i> {{
                            $LANG.Get('buttons.cancel')
                        }}
                    </NevsButton>
                    <NevsButton class="success" @click="productPopup.save()"><i class="fa-solid fa-check"></i> {{ $LANG.Get('buttons.ok') }}</NevsButton>
                </div>
            </div>
        </div>
        <div class="form">
            <div v-if="showGroupSelection">
                <NevsSelect :width="'300px'" v-model="currentGroup" :ajax="'select/product-groups'" :nullable="true"></NevsSelect>
                <span v-if="currentGroup !== null" :title="$LANG.Get('tooltips.deleteProductGroup')" class="group-button" @click="deleteGroup"><i
                    class="fa-solid fa-trash"></i></span>
                <span v-if="currentGroup !== null" :title="$LANG.Get('tooltips.editProductGroup')" class="group-button" @click="editGroup"><i
                    class="fa-solid fa-pen"></i></span>
                <span :title="$LANG.Get('tooltips.addProductGroup')" class="group-button" @click="addGroup"><i
                    class="fa-solid fa-plus"></i></span>
            </div>
        </div>
        <NevsCard v-if="productsReady">
            <NevsTextField style="float: left;" :label="$LANG.Get('labels.filter') + ':'" :width="'300px'" v-model="productsData.filters.name"></NevsTextField>
            <NevsCheckbox style="float: left; margin-left: 15px;" :width="'auto'" :reserveHeights="true" v-model="productsData.filters.show_inactive" :label="$LANG.Get('labels.showInactiveProducts')"></NevsCheckbox>
            <span style="float: right;"><NevsButton class="primary" @click="addProduct"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.addProduct') }}</NevsButton></span>
            <div style="clear: both;"></div>

            <NevsTinyGrid :columns="gridColumns" :records="productsData.records" :actions="gridActions"
                          :is-inactive="(product) => !product.active" @action="gridAction"></NevsTinyGrid>
        </NevsCard>
        <div v-if="!productsReady" class="empty-message">
            {{ $LANG.Get('texts.selectProductGroup') }}
        </div>
    </div>
</template>

<script>

import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsNumberField from "@/components/nevs/NevsNumberField.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";
import NevsTinyGrid from "@/components/nevs/NevsTinyGrid.vue";

export default {
    name: "ModuleProducts",
    components: {NevsTinyGrid, NevsCheckbox, NevsSelect, NevsNumberField, NevsTextField, NevsButton, NevsCard},
    data() {
        return {
            screenReady: false,
            showGroupSelection: true,
            productsReady: false,
            currentGroup: null,
            productsData: {
                filters: {
                    name: '',
                    product_group_id: null,
                    show_inactive: false
                },
                records: [],
                filterTimer: null
            },
            productPopup: {
                show: false,
                data: {
                    id: 0,
                    code: '',
                    code_kpd: '',
                    name: '',
                    name_en: '',
                    active: true,
                    measurement_unit_id: (this.$store.state.user.client.default_measurement_unit_id !== null) ? this.$store.state.user.client.default_measurement_unit_id : 1,
                    price: 0,
                    tax: this.$store.state.settings.DEFAULT_TAX,
                    return_fee: false,
                    return_fee_amount: 0,
                    wares_expenditure: false,
                    wares_expenditure_details: []
                },
                validation: {
                    name: '',
                    price: '',
                    return_fee_amount: ''
                },
                reset: () => {
                    this.productPopup.data = {
                        id: 0,
                        code: '',
                        code_kpd: '',
                        name: '',
                        name_en: '',
                        active: true,
                        measurement_unit_id: (this.$store.state.user.client.default_measurement_unit_id !== null) ? this.$store.state.user.client.default_measurement_unit_id : 1,
                        product_group_id: 0,
                        price: 0,
                        tax: this.$store.state.settings.DEFAULT_TAX,
                        return_fee: false,
                        return_fee_amount: 0,
                        wares_expenditure: false,
                        wares_expenditure_details: []
                    };
                    this.productPopup.validation = {
                        name: '',
                        price: '',
                        return_fee_amount: ''
                    }
                },
                cancel: () => {
                    this.productPopup.show = false;
                },
                save: () => {
                    let success = true;
                    if (this.productPopup.data.name === '') {
                        success = false;
                        this.productPopup.validation.name = this.$LANG.Get('labels.mandatoryField');
                    } else {
                        this.productPopup.validation.name = '';
                    }
                    if (this.productPopup.data.price < 0) {
                        success = false;
                        this.productPopup.validation.price = this.$LANG.Get('labels.errorNegativeValue');
                    } else {
                        this.productPopup.validation.price = '';
                    }
                    if (this.productPopup.data.return_fee && !(Number(this.productPopup.data.return_fee_amount) > 0)) {
                        success = false;
                        this.productPopup.validation.return_fee_amount = this.$LANG.Get('labels.errorReturnFeeAmount');
                    } else {
                        this.productPopup.validation.return_fee_amount = '';
                    }
                    if (this.productPopup.data.wares_expenditure) {
                        if (this.productPopup.data.wares_expenditure_details.length > 0) {
                            for (let ware of this.productPopup.data.wares_expenditure_details) {
                                if (ware.quantity < 0) {
                                    success = false;
                                    this.$LOCAL_BUS.TriggerEvent('popup', {
                                        text: this.$LANG.Get('alerts.wareQuantityError'),
                                        type: 'alert'
                                    });
                                    break;
                                }
                            }
                        } else {
                            success = false;
                            this.$LOCAL_BUS.TriggerEvent('popup', {
                                text: this.$LANG.Get('alerts.wareExpenditureDetailsEmpty'),
                                type: 'alert'
                            });
                        }
                    }
                    if (success) {
                        for (let index in this.productPopup.data.wares_expenditure_details) {
                            if (this.productPopup.data.wares_expenditure_details[index].ware_id === null) {
                                this.productPopup.data.wares_expenditure_details.splice(index, 1);
                            }
                        }
                        this.productPopup.data.product_group_id = this.currentGroup;
                        let method = (this.productPopup.data.id === 0) ? 'post' : 'put';
                        let action = (this.productPopup.data.id === 0) ? 'products' : 'products/' + this.productPopup.data.id;
                        let vm = this;
                        this.$API.APICall(method, action, this.productPopup.data, (data, success) => {
                            vm.productPopup.show = false;
                            vm.productPopup.reset();
                            if (success) {
                                vm.$LOCAL_BUS.TriggerEvent('notification', {text: this.$LANG.Get('alerts.recordSaved')});
                                vm.reloadProducts();
                            } else if (data && data.error === 'return_fee_in_payment_plan') {
                                vm.$LOCAL_BUS.TriggerEvent('popup', {
                                    text: vm.$LANG.Get('alerts.returnFeeInPaymentPlan'),
                                    type: 'alert'
                                });
                            } else if (data && data.error === 'wares_expenditure_in_use') {
                                vm.$LOCAL_BUS.TriggerEvent('popup', {
                                    text: vm.$LANG.Get('alerts.waresExpenditureInUse'),
                                    type: 'alert'
                                });
                            } else {
                                vm.$HELPERS.HandleServerError(vm, data);
                            }
                        });
                    }
                },
                addWare: () => {
                    this.productPopup.data.wares_expenditure_details.push({
                        ware_id: null,
                        quantity: 0
                    });
                },
                removeWare: (index) => {
                    let vm = this;
                    this.$LOCAL_BUS.TriggerEvent('popup', {
                        type: 'confirm', text: this.$LANG.Get('alerts.generalRemovalQuestion'), callback: (response) => {
                            if (response) {
                                vm.productPopup.data.wares_expenditure_details.splice(index, 1);
                            }
                        }
                    });
                }
            }
        }
    },
    computed: {
        gridColumns() {
            return [
                {key: 'name_display', label: this.$LANG.Get('fields.name')},
                {
                    key: 'price_display',
                    label: this.$LANG.Get('fields.priceWithoutTax').split('%currency%').join(this.$HELPERS.GetDefaultCurrency()),
                    align: 'right'
                }
            ];
        },
        gridActions() {
            return [
                {name: 'edit', icon: 'fa-solid fa-pen', tooltip: this.$LANG.Get('tooltips.editProduct')}
            ];
        }
    },
    watch: {
        'currentGroup': {
            handler() {
                this.productsReady = false;
                this.$nextTick(() => {
                    this.productsData.records = [];
                    this.productsData.filters.name = '';
                    if (this.currentGroup !== null) {
                        this.reloadProducts();
                    }
                });
            },
            deep: true
        },
        'productsData.filters.name': {
            handler() {
                if (this.filterTimer !== null) {
                    clearTimeout(this.filterTimer);
                }
                let vm = this;
                this.filterTimer = setTimeout(function () {
                    vm.reloadProducts();
                    vm.filterTimerOn = false;
                }, 500);
            },
            deep: true
        },
        'productsData.filters.show_inactive': {
            handler() {
                this.reloadProducts();
            }
        }
    },
    methods: {
        gridAction(name, product) {
            if (name === 'edit') {
                this.editProduct(product);
            }
        },
        editProduct(product) {
            let vm = this;
            this.$API.APICall('get', 'products/' + product.id, {}, (data, success) => {
                if (success && data.product !== null) {
                    vm.productPopup.reset();
                    vm.productPopup.data = JSON.parse(JSON.stringify(data.product));
                    vm.productPopup.show = true;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        addProduct() {
            this.productPopup.reset();
            this.productPopup.show = true;
        },
        deleteGroup() {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.productGroupDeletionQuestion'), callback: (response) => {
                    if (response) {
                        this.$API.APICall('delete', 'product-groups/' + this.currentGroup, {}, (data, success) => {
                            if (success) {
                                vm.showGroupSelection = false;
                                vm.currentGroup = null;
                                vm.$nextTick(()=>{
                                   vm.showGroupSelection = true;
                                });
                            } else if (data.error === 'has_products') {
                                vm.$LOCAL_BUS.TriggerEvent('popup', {
                                    text: vm.$LANG.Get('alerts.productGroupHasProducts'),
                                    type: 'alert'
                                });
                            } else {
                                vm.$HELPERS.HandleServerError(vm, data);
                            }
                        });
                    }
                }
            });
        },
        addGroup() {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'input', text: vm.$LANG.Get('alerts.addGroup'), callback: (response) => {
                    if (response !== null && response !== '') {
                        this.$API.APICall('post', 'product-groups', {name: response}, (data, success) => {
                            if (success) {
                                vm.$LOCAL_BUS.TriggerEvent('notification', {text: this.$LANG.Get('alerts.recordSaved')});
                                vm.showGroupSelection = false;
                                vm.currentGroup = data.product_group.id;
                                vm.$nextTick(()=>{
                                    vm.showGroupSelection = true;
                                });
                            } else {
                                vm.$HELPERS.HandleServerError(vm, data);
                            }
                        });
                    }
                }
            });
        },
        editGroup() {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'input', text: vm.$LANG.Get('alerts.editGroup'), callback: (response) => {
                    if (response !== null && response !== '') {
                        this.$API.APICall('put', 'product-groups/' + vm.currentGroup, {name: response}, (data, success) => {
                            if (success) {
                                vm.showGroupSelection = false;
                                vm.$nextTick(()=>{
                                    vm.showGroupSelection = true;
                                });
                            } else {
                                vm.$HELPERS.HandleServerError(vm, data);
                            }
                        });
                    }
                }
            });
        },
        reloadProducts() {
            this.productsData.filters.product_group_id = this.currentGroup;
            let vm = this;
            let request = {
                sort: {
                    field: 'name',
                    descending: false
                },
                filters: this.productsData.filters,
                currentPage: 1,
                rowsPerPage: 0
            };
            this.$API.APICall('get', 'products', request, (data, success) => {
                if (success) {
                    vm.productsReady = true;
                    vm.productsData.records = data.records;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    },
    mounted() {

        this.$store.commit('selectMenu', 'catalog');
        this.$store.commit('selectSubMenu', 'products');
        this.$store.commit('setBreadcrumbs', [
            {
                label: this.$LANG.Get('modules.catalog'),
                link: null
            },
            {
                label: this.$LANG.Get('modules.products'),
                link: null
            }
        ]);

        let vm = this;
        this.$API.APICall('get', 'select/product-groups', {}, (data, success) => {
            if (success) {
                let nonNull = null;
                for (let record of data) {
                    if (record.value !== null) {
                        nonNull = record.value;
                        break;
                    }
                }
                if (nonNull !== null) {
                    vm.currentGroup = nonNull;
                    vm.$nextTick(() => {
                        vm.screenReady = true;
                    });
                } else {
                    vm.screenReady = true;
                }
            } else {
                vm.$HELPERS.HandleServerError(vm, data);
            }
        });
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;
@use '../../scss/grids' as g;

.form {
    width: 100%;
    margin-bottom: 10px;
    margin-right: 10px;
}

.form .nevs-button {
    margin-left: 5px;
}

.group-button {
    line-height: 54px;
    margin-right: 10px;
    font-size: 14px;
    cursor: pointer;
}

.product-popup-half {
    width: 50% !important;
}

.return-fee-disclaimer {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background-color: $warning-surface;
    border: 1px solid $warning-border;
    color: $warning-ink;
    border-radius: 5px;
    padding: 12px 14px;
    margin: 5px 7px 10px 7px;
    font-size: 0.9em;
    line-height: 1.45;
}

.return-fee-disclaimer i {
    margin-top: 2px;
    flex-shrink: 0;
}

.empty-message {
    padding-left: 5px;
    margin-top: 20px;
    font-size: 20px;
}

.wares-details-table {
    width: 100%;
    border-collapse: collapse;
}

@media only screen and (max-width: 1400px) {
    .product-popup-half {
        width: 100% !important;
    }
}

@media only screen and (max-width: 439px) {
    .form {
        min-width: 0;
        width: calc(100% - 20px);
    }

}

</style>